import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { axiosConfig, profile } from 'api';
import { AxiosResponse } from 'axios';
import { RootState, MyThunkDispatch } from 'store';

import { fetchNationalityCountryList } from 'actions/i18n';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { Group } from 'models/group';
import GuestForm from 'screens/components/GuestForm';
import { Guest, createUserGuest } from 'services/userGuestService';
import { extractErrorMessage, formatDateToBack } from 'utils/generic';
import { doSetGroups } from 'action-creators/travelArrangerGuest';

interface Props {
  show: boolean;
  onSaveTraveler: (guest: Guest) => void;
  onClose: () => void;
}

const GuestFormContainer = ({ show, onSaveTraveler, onClose }: Props) => {
  const toast = useToast();
  const { t } = useStoreTranslation();
  const [loading, setLoading] = useState(false);
  const dispatch: MyThunkDispatch = useDispatch();
  const [groupList, setGroupList] = useState<Group[]>([]);
  const {
    i18n: { loadingNationalityCountries, nationalityCountries },
  } = useSelector((state: RootState) => state);

  const {
    travelArrangerGuest: { guest },
  } = useSelector((state: RootState) => state);

  useEffect(() => {
    if (!nationalityCountries.length && !loadingNationalityCountries) {
      dispatch(fetchNationalityCountryList());
    }
  }, [dispatch, nationalityCountries, loadingNationalityCountries]);

  const handleSubmit = async (guest: Guest) => {
    const birthDate = `${guest.birthDate_day}/${guest.birthDate_month}/${guest.birthDate_year}`;

    const newGuest: Guest = {
      ...guest,
      email: guest.email.trim(),
      birthDate: formatDateToBack(birthDate),
    };
    setLoading(true);

    try {
      const created = await createUserGuest(newGuest);
      onSaveTraveler(created);
      onClose();
    } catch (errors) {
      if (errors?.response?.status === 409) {
        toast.add({ type: 'danger', message: t('guestForm.alreadyExist') });
      } else {
        toast.add({ type: 'danger', message: extractErrorMessage(errors) });
      }
    } finally {
      setLoading(false);
    }
  };

  const getGroups = async () => {
    setLoading(true);
    try {
      const {
        data: { data },
      } = await profile.get<AxiosResponse<Group[]>>('/UserGroup/GetAll', axiosConfig);
      dispatch(doSetGroups(data));
      setGroupList(data);
    } catch (error) {
      toast.add({
        type: 'danger',
        message: t('group.groupAggregation.errorFetch'),
      });
      setLoading(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    getGroups();    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <GuestForm
      loading={loading}
      show={show}
      onClose={onClose}
      onSubmit={handleSubmit}
      countries={nationalityCountries}
      groups={groupList}
      travelArrangerGuest={guest}
    />
  );
};

export default GuestFormContainer;
